import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { NavigationService, Position } from '@client/services/NavigationService';
import {
  useNavigationStateMachine,
  NavigationMachineState,
} from '@client/hooks/useNavigationStateMachine';
import {
  savePolicyApplicationFormSubmission,
  usePolicyApplicationFormSubmission,
} from '@client/hooks/usePolicyApplicationFormSubmission';
import { useDispatch } from 'react-redux';
import { resetPolicyApplicationStateStatus } from '@/model/policyApplicationModel';
import { useToast } from '@/components/hooks/use-toast';
import { useNavigate } from 'react-router';
import { cleanFormDataForHiddenQuestions } from '@client/utils/formDataUtils';
import {
  applyCompositeFields,
  calculateCompositeFieldValues,
} from '@shared/policy-application/compositeFieldUtils';
import { isRequiredFieldError } from '@client/util/questionValidation';
import { useFormConditionals } from '@client/hooks/useFormConditionals';
import { usePrefillDataForForm } from '@client/hooks/usePrefillDataForForm';
import { usePrefillCitationsForForm } from '@client/hooks/usePrefillCitationsForForm';
import { FormParameterValues } from '@client/hooks/useFormParameters';

interface NavigationContextValue {
  state: NavigationMachineState;
  navigationService: NavigationService;
  formDefinition: PolicyFormDefinition;
  clientPolicyApplicationId: string;
  animationDirection: number;
  actions: {
    navigateTo: (position: Position) => void;
    navigateNext: (force: boolean) => void;
    navigatePrevious: () => void;
    updateFormData: (questionRef: string, value: PolicyFormSubmissionDataValue) => void;
    markVisited: (subsectionId: string) => void;
    initialize: (
      formData: PolicyFormSubmissionData,
      visitedSubsections?: string[],
      initialPosition?: Position,
    ) => void;
    clearError: () => void;
    showValidationErrors: () => void;
    setFieldValidationError: (questionRef: string, error: string | undefined) => void;
    clearFieldValidationErrors: () => void;
    recomputeAllWarnings: (
      formData: PolicyFormSubmissionData,
      visitedSubsections: Set<string>,
      parameters: FormParameterValues,
    ) => void;
  };
  computed: {
    canGoNext: boolean;
    canGoPrevious: boolean;
    isLastStep: boolean;
    isLoading: boolean;
    isSaving: boolean;
    hasError: boolean;
  };
  getCurrentSubsection: () => PolicyFormSubSection | null;
  getCurrentSection: () => PolicyFormSection | null;
  validationState: {
    // Flag to trigger required field validation checks (set true when user clicks Next)
    showValidationErrors: boolean;
    // List of required fields that are currently empty (computed by NavigationService)
    missingRequiredFields: Array<{
      questionRef: string;
      question: PolicyFormQuestion;
    }>;
    // Map of all field validation errors (both format and required field errors)
    // Key: questionRef, Value: error message
    fieldValidationErrors: Map<string, string>;
  };
  prefillCitations?: Record<string, PrefillCitationDef>;
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}

interface NavigationProviderProps {
  children: ReactNode;
  formDefinition: PolicyFormDefinition;
  formId: string;
  clientId: string;
  clientPolicyApplicationId: string;
  parameters?: Record<string, any>;
  forClientId: string;
  wizardUnrestrictedNavEnabled?: boolean;
}

export function NavigationProvider({
  children,
  formDefinition,
  formId,
  clientId,
  clientPolicyApplicationId,
  parameters = {},
  forClientId,
  wizardUnrestrictedNavEnabled,
}: NavigationProviderProps) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const navigate = useNavigate();

  const { form, status } = usePolicyApplicationFormSubmission(
    clientId,
    clientPolicyApplicationId,
    formId,
    forClientId,
  );
  const submission = form;

  const [animationDirection, setAnimationDirection] = useState(1);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [fieldValidationErrors, setFieldValidationErrors] = useState<Map<string, string>>(
    new Map(),
  );

  const navigationService = useMemo(() => {
    return new NavigationService(
      formDefinition,
      formDefinition.questions,
      wizardUnrestrictedNavEnabled ?? false,
    );
  }, [formDefinition, formDefinition?.questions, wizardUnrestrictedNavEnabled]);

  const { state, actions, computed } = useNavigationStateMachine(
    navigationService,
    parameters,
    wizardUnrestrictedNavEnabled ?? false,
  );

  // Use the established hook pattern for conditional logic
  const conditionalService = useFormConditionals(formDefinition, state.formData, parameters);

  const formPrefillData = usePrefillDataForForm();

  //Moved this to the top to use in custom hook
  const getCurrentSubsection = useCallback((): PolicyFormSubSection | null => {
    if (!state.currentPosition) return null;

    const section = formDefinition.sections[state.currentPosition.sectionIndex];
    return section?.subsections[state.currentPosition.subsectionIndex] || null;
  }, [state.currentPosition, formDefinition.sections]);

  const currentSubsection = useMemo(() => getCurrentSubsection(), [getCurrentSubsection]);

  // Extract only the citations relevant to the current subsection and visible questions

  const conditionalCitations = usePrefillCitationsForForm(
    formPrefillData,
    conditionalService,
    currentSubsection,
    state.formData,
  );

  // Update NavigationService context when form data or parameters change
  useEffect(() => {
    navigationService.updateContext(state.formData, parameters);
  }, [navigationService, state.formData, parameters]);

  // Clear validation errors for fields that are no longer visible or required when form data changes
  useEffect(() => {
    if (fieldValidationErrors.size === 0) return; // Early exit if no errors to clear

    // Check both visibility and required state for fields that currently have errors
    const fieldsToRemove: string[] = [];
    for (const [errorKey, errorMessage] of fieldValidationErrors.entries()) {
      // Check if this is a table cell error (format: "questionRef:rowIndex:columnId")
      const isTableCellError = errorKey.includes(':');

      if (isTableCellError) {
        // Extract the base questionRef from the cell error key
        const baseQuestionRef = errorKey.split(':')[0];
        const question = formDefinition.questions[baseQuestionRef];

        if (question) {
          const questionState = conditionalService.getQuestionState(question);
          // Remove table cell errors if the parent table question is not visible
          if (!questionState.visible) {
            fieldsToRemove.push(errorKey);
          }
        }
      } else {
        // Regular question error
        const question = formDefinition.questions[errorKey];
        if (question) {
          const questionState = conditionalService.getQuestionState(question);

          // Remove errors if field is not visible OR not required (and error is requirement-related)
          const shouldRemove =
            !questionState.visible ||
            (isRequiredFieldError(errorMessage) && !questionState.required);

          if (shouldRemove) {
            fieldsToRemove.push(errorKey);
          }
        }
      }
    }

    // Remove errors for hidden or no-longer-required fields
    if (fieldsToRemove.length > 0) {
      setFieldValidationErrors(prev => {
        const newErrors = new Map(prev);
        fieldsToRemove.forEach(errorKey => newErrors.delete(errorKey));
        return newErrors;
      });
    }
  }, [state.formData, parameters, formDefinition, conditionalService, fieldValidationErrors]);

  useEffect(() => {
    const hasSubmissionData =
      submission?.submissionData && Object.keys(submission.submissionData).length > 0;
    const hasPrefillData = formPrefillData?.prefillData && formPrefillData?.prefillData?.length > 0;
    const isFormDataEmpty = Object.keys(state.formData).length === 0;

    if (
      (status === 'fetchingComplete' || form !== null) &&
      formDefinition &&
      !navigationService.isInitialized &&
      (state.type === 'loading' || (isFormDataEmpty && (hasSubmissionData || hasPrefillData)))
    ) {
      const visitedSubsections = submission?.visitedSubsections ?? [];
      const initialPosition = navigationService.getFirstPosition();

      // Create a new object for form data to avoid mutating frozen objects
      const initialFormData = { ...(submission?.submissionData ?? {}) };

      // If we have prefill data and no submission data for specific fields, use prefill
      if (formPrefillData && Array.isArray(formPrefillData.prefillData)) {
        formPrefillData.prefillData.forEach(prefillItem => {
          // Only use prefill if there's no existing submission data for this field

          if (
            initialFormData[prefillItem.questionRef] === undefined ||
            initialFormData[prefillItem.questionRef] === null ||
            initialFormData[prefillItem.questionRef] === ''
          ) {
            initialFormData[prefillItem.questionRef] = prefillItem.answer;
          }
        });
      }

      navigationService.updateContext(initialFormData, parameters);
      navigationService.isInitialized = true;
      actions.initialize(initialFormData, visitedSubsections, initialPosition);
      navigationService.recomputeAllWarnings(
        initialFormData,
        new Set(visitedSubsections),
        parameters,
      );
    }
  }, [
    status,
    form,
    formDefinition,
    submission,
    formPrefillData,
    navigationService,
    actions,
    state.formData,
    state.type,
    parameters,
  ]);

  useEffect(() => {
    if (status === 'savingComplete') {
      //Did not remove this code as it may be needed later

      // if (state.progress?.isFormComplete) {
      //   toast({
      //     title: '🎉 Form Complete',
      //     description: 'You can now send your form.',
      //     className: '-translate-y-5 bg-primary text-white',
      //   });
      //   navigate(`/clients/${clientId}/policy-application/${clientPolicyApplicationId}`);
      // } else {
      toast({
        title: 'Progress Saved',
        description: 'Your progress has been saved automatically.',
        variant: 'default',
        className: '-translate-y-5',
        duration: 3000,
      });

      actions.saveSuccess();
      //}
      dispatch(resetPolicyApplicationStateStatus());
    }
  }, [
    status,
    state.progress?.isFormComplete,
    clientId,
    formId,
    actions,
    clientPolicyApplicationId,
  ]);

  useEffect(() => {
    if (status === 'error') {
      actions.saveError('Failed to save form data');
      dispatch(resetPolicyApplicationStateStatus());
    }
  }, [status, actions, dispatch]);

  const updateFormData = (questionRef: string, value: PolicyFormSubmissionDataValue) => {
    let updatedFormData = {
      ...state.formData,
      [questionRef]: value,
    };

    updatedFormData = calculateCompositeFieldValues(
      updatedFormData,
      formDefinition.questions,
      questionRef,
    );

    actions.updateFormData(updatedFormData);
  };

  const clearErrorMessage = () => {
    setShowValidationErrors(false);
    setFieldValidationErrors(new Map());
  };

  const handleNavigateNext = (force: boolean = false) => {
    setAnimationDirection(1); // Forward direction
    clearErrorMessage();
    if (state.currentPosition) {
      const validation = navigationService.canNavigateFromPosition(
        state.currentPosition,
        state.formData,
        parameters,
      );

      const hasErrors = !validation.canNavigate;

      const visitedArray = Array.from(state.visitedSubsections);

      const dataWithCompositeFields = applyCompositeFields(
        state.formData,
        formDefinition.questions,
      );

      if (force && hasErrors) {
        navigationService.markSubsectionError(state.currentPosition.subsectionId, 'error');
      }

      if (!hasErrors) {
        navigationService.clearSubsectionWarning(state.currentPosition.subsectionId);
      }

      const cleanedFormData = cleanFormDataForHiddenQuestions(
        dataWithCompositeFields,
        formDefinition,
        parameters,
      );
      savePolicyApplicationFormSubmission(
        clientId,
        clientPolicyApplicationId,
        formId,
        cleanedFormData,
        state.progress?.isFormComplete ? 'completed' : 'in_progress',
        visitedArray,
        dispatch,
        forClientId,
      );
    }
    actions.navigateNext(force);
  };

  const handleNavigatePrevious = () => {
    setAnimationDirection(-1); // Backward direction
    clearErrorMessage();
    actions.navigatePrevious();
  };

  const handleNavigateTo = (position: Position) => {
    // Determine direction based on position comparison
    if (state.currentPosition) {
      const currentIndex =
        state.currentPosition.sectionIndex * 1000 + state.currentPosition.subsectionIndex;
      const targetIndex = position.sectionIndex * 1000 + position.subsectionIndex;
      setAnimationDirection(targetIndex > currentIndex ? 1 : -1);
    }
    clearErrorMessage();
    actions.navigateTo(position);
  };

  const getCurrentSection = (): PolicyFormSection | null => {
    if (!state.currentPosition) return null;

    return formDefinition.sections[state.currentPosition.sectionIndex] || null;
  };

  const getMissingRequiredFields = () => {
    const currentSubsection = getCurrentSubsection();
    if (!currentSubsection) return [];

    navigationService.updateContext(state.formData, parameters);
    return navigationService.getMissingRequiredFieldsForSubsection(currentSubsection);
  };

  const handleShowValidationErrors = () => {
    // This flag triggers required field validation checks in QuestionField components.
    // When true, empty required fields will show "This field is required" errors.
    // Format validation (email, phone, etc.) happens independently in real-time.
    setShowValidationErrors(true);
  };

  const handleSetFieldValidationError = (questionRef: string, error: string | undefined) => {
    setFieldValidationErrors(prev => {
      const newErrors = new Map(prev);
      if (error) {
        newErrors.set(questionRef, error);
      } else {
        newErrors.delete(questionRef);
      }
      return newErrors;
    });
  };

  const handleClearFieldValidationErrors = () => {
    setFieldValidationErrors(new Map());
  };

  const handleRecomputeAllWarnings = (
    formData: PolicyFormSubmissionData,
    visitedSubsections: Set<string>,
    parameters: FormParameterValues,
  ) => {
    navigationService.recomputeAllWarnings(formData, visitedSubsections, parameters);
  };

  const contextValue: NavigationContextValue = {
    state,
    navigationService,
    formDefinition,
    clientPolicyApplicationId,
    animationDirection,
    actions: {
      navigateTo: handleNavigateTo,
      navigateNext: handleNavigateNext,
      navigatePrevious: handleNavigatePrevious,
      updateFormData,
      markVisited: actions.markVisited,
      clearError: actions.clearError,
      initialize: actions.initialize,
      showValidationErrors: handleShowValidationErrors,
      setFieldValidationError: handleSetFieldValidationError,
      clearFieldValidationErrors: handleClearFieldValidationErrors,
      recomputeAllWarnings: handleRecomputeAllWarnings,
    },
    computed,
    getCurrentSubsection,
    getCurrentSection,
    validationState: {
      showValidationErrors,
      missingRequiredFields: getMissingRequiredFields(),
      fieldValidationErrors,
    },
    prefillCitations: conditionalCitations,
  };

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
}
